import {
  ClipboardCheckIcon,
  MoonIcon,
  GaugeIcon,
  GitBranchIcon,
  ScanSearchIcon,
  FileCheck2Icon,
  WrenchIcon,
  CarIcon,
  ZapIcon,
  ActivityIcon,
} from 'lucide-react';

export const CHECKOUT_URL = 'https://parasitic-en.impultienda.ar/checkout/';
export const MOCKUP_URL =
  'https://cdn.magicpatterns.com/uploads/aq9kfWAt9yvppPDovpqdbL/MOCKUP_PARASITIC-EN.png';
export const AUTHOR_URL =
  'https://cdn.magicpatterns.com/uploads/37LWThduLENiv6PFRCbJMA/JASON_MITCHELL_-_ECU_DECISION.png';

export const heroChecks = [
  '6 Practical Diagnostic Volumes',
  '5 Ready-to-Use Diagnostic Bonuses',
  'Step-by-Step Testing Workflows',
  'Circuit Isolation Decision Trees',
  'Professional Checklists & Worksheets',
  'Instant Digital Access',
];

export const painPoints = [
  'Repeated battery replacements without identifying the underlying issue.',
  'Electrical measurements taken before vehicle modules enter sleep mode.',
  'Unnecessary circuit disconnections that interrupt the test.',
  'Hours spent investigating unrelated electrical components.',
  'Inconsistent measurements that are difficult to interpret.',
  'Diagnostic findings that are difficult to explain to customers.',
];

export const steps = [
  {
    name: 'Verify',
    icon: ClipboardCheckIcon,
    lead: 'Confirm the complaint and establish the initial diagnostic conditions.',
    body: 'Use structured intake questions, battery condition checks, and preliminary electrical observations.',
  },
  {
    name: 'Prepare',
    icon: MoonIcon,
    lead: 'Establish appropriate vehicle test conditions.',
    body: 'Account for vehicle-specific shutdown behavior, module activity, access requirements, and test equipment limitations.',
  },
  {
    name: 'Measure',
    icon: GaugeIcon,
    lead: 'Select an appropriate measurement method.',
    body: 'Organize current-draw testing while minimizing interference with the vehicle’s electrical sleep state.',
  },
  {
    name: 'Isolate',
    icon: GitBranchIcon,
    lead: 'Narrow the investigation to potential circuits and electrical systems.',
    body: 'Use structured decision trees and suitable circuit-isolation methods instead of random component replacement.',
  },
  {
    name: 'Investigate',
    icon: ScanSearchIcon,
    lead: 'Evaluate suspicious findings.',
    body: 'Distinguish between potentially abnormal electrical behavior, expected module activity, and measurements requiring further confirmation.',
  },
  {
    name: 'Verify & Document',
    icon: FileCheck2Icon,
    lead: 'Record findings, evaluate corrective actions, and organize final verification.',
    body: 'Create a clear diagnostic record that supports professional repair decisions and customer communication.',
  },
];

export const volumes = [
  {
    title: 'Battery Drain Diagnostic Intake & Verification Blueprint',
    tagline: 'Start with the right checks before investigating electrical circuits.',
    items: [
      'Customer complaint intake checklist',
      'Battery condition verification workflow',
      'Charging-system screening checklist',
      'Battery discharge history worksheet',
      'Initial diagnostic decision tree',
      'Test-condition preparation checklist',
    ],
    outcome: 'A documented starting point for the investigation.',
  },
  {
    title: 'Vehicle Sleep Mode & Electrical Shutdown Assessment Blueprint',
    tagline: 'Understand when the vehicle is ready for meaningful parasitic draw testing.',
    items: [
      'Vehicle shutdown preparation workflow',
      'Module sleep-state observation checklist',
      'Keyless-entry and accessory interference checklist',
      'Electrical stabilization tracking worksheet',
      'Wake-up event documentation template',
      'Sleep-state decision flowchart',
    ],
    outcome: 'A structured process for evaluating whether testing conditions are appropriate.',
  },
  {
    title: 'Parasitic Current Measurement Blueprint',
    tagline: 'Organize electrical measurements without relying on guesswork.',
    items: [
      'Measurement method selection tree',
      'Current-clamp testing checklist',
      'Inline ammeter setup considerations',
      'Equipment protection and test-safety checklist',
      'Current-draw observation log',
      'Measurement interpretation worksheet',
    ],
    outcome:
      'A repeatable process for selecting, performing, and documenting suitable electrical measurements.',
  },
  {
    title: 'Circuit Isolation & Fault Localization Blueprint',
    tagline: 'Move from an unexplained electrical draw toward a focused investigation.',
    items: [
      'Circuit isolation decision trees',
      'Fuse voltage-drop testing workflow',
      'Fuse-removal limitations checklist',
      'Electrical distribution mapping worksheet',
      'Suspect circuit prioritization matrix',
      'Circuit investigation tracking sheet',
    ],
    outcome: 'An organized approach to narrowing down potentially responsible electrical circuits.',
  },
  {
    title: 'Intermittent Drain & Module Wake-Up Investigation Blueprint',
    tagline: 'Bring structure to battery drain problems that don’t appear consistently.',
    items: [
      'Intermittent drain investigation roadmap',
      'Module wake-up observation checklist',
      'Time-based current monitoring worksheet',
      'Accessory and aftermarket equipment checklist',
      'Event correlation documentation template',
      'Intermittent fault decision tree',
    ],
    outcome: 'A structured investigation plan for electrical problems that are difficult to reproduce.',
  },
  {
    title: 'Diagnostic Confirmation & Repair Documentation Blueprint',
    tagline: 'Turn electrical findings into a professional diagnostic record.',
    items: [
      'Root-cause evidence checklist',
      'Before-and-after measurement worksheet',
      'Corrective action verification workflow',
      'Customer explanation template',
      'Final diagnostic report',
      'Repair documentation checklist',
    ],
    outcome: 'A clear record of the investigation, supporting evidence, and verification results.',
  },
];

export const bonuses = [
  {
    title: 'Parasitic Drain Quick Decision Tree',
    value: 19,
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/9a72783c-32eb-4cf4-89dc-62f45656d662.jpg',
    body: 'A visual decision guide that helps organize the next appropriate diagnostic step based on initial findings.',
  },
  {
    title: 'Vehicle Sleep Mode Preparation Checklist',
    value: 17,
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/93d73953-13e3-4f8f-9644-77d67497200c.jpg',
    body: 'A practical checklist for preparing a vehicle for current-draw testing and documenting potential interference.',
  },
  {
    title: 'Electrical Current Measurement Logbook',
    value: 19,
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/07c538e9-326a-4a26-89d4-67472d3a979c.jpg',
    body: 'Printable worksheets for recording measurements, timestamps, vehicle conditions, and relevant observations.',
  },
  {
    title: 'Suspect Circuit Investigation Tracker',
    value: 17,
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/89ad815a-9590-4e2d-9d26-7909ffdb9e69.jpg',
    body: 'A structured worksheet for recording tested circuits, observed changes, and follow-up investigations.',
  },
  {
    title: 'Professional Battery Drain Diagnostic Report Template',
    value: 27,
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b1a7bccf-f1af-44c7-955a-cd5515671dc0.jpg',
    body: 'A ready-to-use reporting format for documenting diagnostic findings and explaining completed testing.',
  },
];

export const withoutBlueprint = [
  'Jumping between electrical circuits',
  'Repeating measurements without consistent conditions',
  'Confusing normal module activity with possible faults',
  'Losing track of previous test results',
  'Replacing components before gathering sufficient evidence',
  'Struggling to document the investigation',
];

export const withBlueprint = [
  'Follow a defined six-stage diagnostic sequence',
  'Prepare the vehicle before measuring current draw',
  'Select suitable measurement procedures',
  'Use decision trees to organize circuit investigations',
  'Record findings with professional worksheets',
  'Verify and communicate diagnostic results',
];

export const audiences = [
  {
    title: 'Automotive Technicians',
    icon: WrenchIcon,
    body: 'Who regularly investigate vehicles with unexplained battery discharge complaints.',
  },
  {
    title: 'Independent Mechanics',
    icon: CarIcon,
    body: 'Who want a more organized process for handling electrical diagnostic jobs.',
  },
  {
    title: 'Automotive Electricians',
    icon: ZapIcon,
    body: 'Who need structured procedures for current-draw testing and circuit investigation.',
  },
  {
    title: 'Diagnostic Specialists',
    icon: ActivityIcon,
    body: 'Who want practical documentation tools and repeatable investigation workflows.',
  },
];

export const testimonials = [
  {
    name: 'Luis Jiménez',
    role: 'Owner & Technician, Jiménez Auto Care',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/26bd0431-5b46-4a32-b0c9-d02ec40b8615.jpg',
    quote:
      'An SUV came back three times with a dead battery. The sleep-mode checklist showed me I was measuring before the modules had shut down. Once I followed the sequence, the fuse voltage-drop workflow led me to an aftermarket dash cam circuit in under an hour.',
  },
  {
    name: 'Daniela Ruiz',
    role: 'Automotive Electrician',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/00cfe072-be55-476d-a5c3-4b2557054df3.jpg',
    quote:
      'The current-draw observation log changed how I work. Every reading now has a timestamp and the vehicle condition next to it, and the report template makes explaining the repair to customers so much easier.',
  },
  {
    name: 'Roberto Salas',
    role: 'Independent Mechanic',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/894b56c5-fe0b-4a4f-a287-38e0a30bf47a.jpg',
    quote:
      'I’ve done electrical work for decades, but never with a written sequence. The intermittent drain roadmap finally gave me a plan for the cars that only die once a week. Worth far more than what I paid.',
  },
];

export const faqs = [
  {
    q: 'Is this a theoretical automotive electricity textbook?',
    a: 'No. This is a practical diagnostic resource focused on structured workflows, decision trees, testing checklists, and documentation templates.',
  },
  {
    q: 'Does it cover modern vehicles with electronic control modules?',
    a: 'Yes. The workflow addresses module sleep behavior, electrical shutdown conditions, intermittent wake-up events, and structured current-draw investigations. Vehicle-specific specifications and procedures must still be obtained from the appropriate manufacturer service information.',
  },
  {
    q: 'Does it provide one universal acceptable parasitic current value?',
    a: 'No. Acceptable current draw varies by vehicle, equipment, and operating conditions. The blueprint emphasizes evaluating measurements against applicable manufacturer specifications.',
  },
  {
    q: 'Can I use it with a digital multimeter?',
    a: 'The blueprint includes measurement-method selection and appropriate testing considerations. Actual procedures depend on your equipment capabilities, vehicle design, and applicable safety requirements.',
  },
  {
    q: 'Will it tell me exactly which component is causing every battery drain?',
    a: 'No diagnostic resource can guarantee identification of every fault. The blueprint provides a structured process for investigating potential causes, evaluating evidence, and determining appropriate next steps.',
  },
  {
    q: 'Is this suitable for beginners?',
    a: 'It is designed primarily for automotive technicians and qualified professionals who already understand basic electrical safety and automotive diagnostic equipment.',
  },
  { q: 'Is this a physical book?', a: 'No. This is a digital product.' },
  {
    q: 'How do I receive the materials?',
    a: 'After successful payment, you’ll receive digital access according to the checkout delivery instructions.',
  },
  { q: 'Is this a subscription?', a: 'No. The advertised $27 purchase is a one-time payment.' },
  {
    q: 'Is there a guarantee?',
    a: 'Yes. Your purchase includes a 7-day money-back guarantee under the stated refund terms.',
  },
];

export const recentBuyers = [
  { name: 'Carlos M.', country: 'Mexico' },
  { name: 'Andrés R.', country: 'Colombia' },
  { name: 'Javier P.', country: 'Spain' },
  { name: 'Martín G.', country: 'Argentina' },
  { name: 'Diego F.', country: 'Chile' },
  { name: 'José L.', country: 'Peru' },
  { name: 'Ricardo V.', country: 'Ecuador' },
  { name: 'Fernando A.', country: 'Uruguay' },
  { name: 'Miguel S.', country: 'Guatemala' },
  { name: 'Luis C.', country: 'Dominican Republic' },
  { name: 'Alejandro T.', country: 'Costa Rica' },
  { name: 'Héctor B.', country: 'Paraguay' },
  { name: 'Raúl N.', country: 'Bolivia' },
  { name: 'Sofía H.', country: 'Panama' },
  { name: 'Eduardo Q.', country: 'Venezuela' },
  { name: 'Pablo D.', country: 'Puerto Rico' },
];
